import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { OnboardingState } from './state';
import { OnboardingCommand } from './commands';

/**
 * Pure handler functions for onboarding commands.
 *
 * Rules:
 * - MUST be pure (no side effects, no API calls)
 * - MUST return new state object (immutable)
 * - MUST NOT dispatch other commands
 */

// =============================================================================
// DATA HANDLERS
// =============================================================================

const handleDataSet = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/data/set' }>
): OnboardingState => {
  return {
    ...state,
    data: {
      ...state.data,
      onboardingCompletedAt: cmd.payload.onboardingCompletedAt,
      accountId: cmd.payload.accountId
    }
  };
};

// =============================================================================
// INVOICING HANDLERS
// =============================================================================

const handleInvoicingSelectConnector = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/invoicing/selectConnector' }>
): OnboardingState => {
  return {
    ...state,
    invoicing: {
      ...state.invoicing,
      selectedConnectorId: cmd.payload.connectorId
    }
  };
};

const handleInvoicingUpdateCredentials = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/invoicing/updateCredentials' }>
): OnboardingState => {
  return {
    ...state,
    invoicing: {
      ...state.invoicing,
      credentials: cmd.payload.credentials
    }
  };
};

const handleInvoicingSubmitStart = (
  state: OnboardingState,
  _cmd: Extract<OnboardingCommand, { type: 'onboarding/invoicing/submit/start' }>
): OnboardingState => {
  // Mark as submitting (policy will handle the backend call).
  return {
    ...state,
    loading: {
      ...state.loading,
      submittingInvoicing: true
    },
    errors: {}
  };
};

const handleInvoicingSubmitSuccess = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/invoicing/submit/success' }>
): OnboardingState => {
  return {
    ...state,
    invoicing: {
      ...state.invoicing,
      provisionedIntegrationId: cmd.payload.integrationId
    },
    loading: {
      ...state.loading,
      submittingInvoicing: false
    }
  };
};

const handleInvoicingSubmitError = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/invoicing/submit/error' }>
): OnboardingState => {
  return {
    ...state,
    loading: {
      ...state.loading,
      submittingInvoicing: false
    },
    errors: {
      ...state.errors,
      invoicing: cmd.payload.error ?? 'No se pudo conectar la integración de facturación'
    }
  };
};

// =============================================================================
// BILLING HANDLERS
// =============================================================================

const handleBillingUpdateField = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/billing/updateField' }>
): OnboardingState => {
  const newForm = {
    ...state.billing.form,
    [cmd.payload.field]: cmd.payload.value
  };

  // Clear error for this field.
  const newErrors = { ...state.billing.errors };
  delete newErrors[cmd.payload.field];

  return {
    ...state,
    billing: {
      ...state.billing,
      form: newForm,
      errors: newErrors
    }
  };
};

const handleBillingSetErrors = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/billing/setErrors' }>
): OnboardingState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      errors: cmd.payload.errors
    }
  };
};

const handleBillingSubmitStart = (
  state: OnboardingState,
  _cmd: Extract<OnboardingCommand, { type: 'onboarding/billing/submit/start' }>
): OnboardingState => {
  // Mark as submitting (policy will handle the backend call).
  return {
    ...state,
    loading: {
      ...state.loading,
      submittingBilling: true
    },
    errors: {}
  };
};

const handleBillingSubmitSuccess = (
  state: OnboardingState,
  _cmd: Extract<OnboardingCommand, { type: 'onboarding/billing/submit/success' }>
): OnboardingState => {
  return {
    ...state,
    loading: {
      ...state.loading,
      submittingBilling: false
    }
  };
};

const handleBillingSubmitError = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/billing/submit/error' }>
): OnboardingState => {
  return {
    ...state,
    loading: {
      ...state.loading,
      submittingBilling: false
    },
    errors: {
      ...state.errors,
      billing: cmd.payload.error ?? 'No se pudo guardar la entidad de facturación'
    }
  };
};

// =============================================================================
// COMPLETE HANDLERS
// =============================================================================

const handleCompleteStart = (
  state: OnboardingState,
  _cmd: Extract<OnboardingCommand, { type: 'onboarding/complete/start' }>
): OnboardingState => {
  // Mark as completing (policy will persist onboardingCompletedAt).
  return {
    ...state,
    loading: {
      ...state.loading,
      completing: true
    },
    errors: {}
  };
};

const handleCompleteSuccess = (
  state: OnboardingState,
  _cmd: Extract<OnboardingCommand, { type: 'onboarding/complete/success' }>
): OnboardingState => {
  // Set onboardingCompletedAt so selectIsOnboardingRequired flips false and
  // the wizard unmounts. The server timestamp is reconciled later by the init
  // policy via the account readmodel sync; we mark it locally to unmount now.
  return {
    ...state,
    data: {
      ...state.data,
      onboardingCompletedAt: state.data.onboardingCompletedAt ?? new Date().toISOString()
    },
    loading: {
      ...state.loading,
      completing: false
    }
  };
};

const handleCompleteError = (
  state: OnboardingState,
  cmd: Extract<OnboardingCommand, { type: 'onboarding/complete/error' }>
): OnboardingState => {
  return {
    ...state,
    loading: {
      ...state.loading,
      completing: false
    },
    errors: {
      ...state.errors,
      complete: cmd.payload.error ?? 'No se pudo completar el onboarding'
    }
  };
};

// =============================================================================
// WIZARD HANDLERS
// =============================================================================

const handleWizardOpen = ( state: OnboardingState ): OnboardingState => ({
  ...state,
  ui: { ...state.ui, manuallyOpened: true }
});

// =============================================================================
// HANDLER MAP
// =============================================================================

export const handlers: CommandHandlerMap<OnboardingState, OnboardingCommand> = {
  // Data
  'onboarding/data/set': handleDataSet,

  // Invoicing
  'onboarding/invoicing/selectConnector': handleInvoicingSelectConnector,
  'onboarding/invoicing/updateCredentials': handleInvoicingUpdateCredentials,
  'onboarding/invoicing/submit/start': handleInvoicingSubmitStart,
  'onboarding/invoicing/submit/success': handleInvoicingSubmitSuccess,
  'onboarding/invoicing/submit/error': handleInvoicingSubmitError,

  // Billing
  'onboarding/billing/updateField': handleBillingUpdateField,
  'onboarding/billing/setErrors': handleBillingSetErrors,
  'onboarding/billing/submit/start': handleBillingSubmitStart,
  'onboarding/billing/submit/success': handleBillingSubmitSuccess,
  'onboarding/billing/submit/error': handleBillingSubmitError,

  // Complete
  'onboarding/complete/start': handleCompleteStart,
  'onboarding/complete/success': handleCompleteSuccess,
  'onboarding/complete/error': handleCompleteError,

  // Wizard
  'onboarding/wizard/open': handleWizardOpen
};
